import '@/assets/tailwind.css'
import 'vue-toast-notification/dist/theme-sugar.css'

import VCalendar from 'v-calendar'
import ToastPlugin from 'vue-toast-notification'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from '@/App.vue'
import router from '@/router'
import oidcAuth, { refreshBoostCoreSession, initAuthListeners } from '@/auth/oidcAuth'
import { AgGridVue } from 'ag-grid-vue3'
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'
import { useUserStore } from '@/stores/useUserStore'
import { useCampaignStore } from '@/stores/useCampaignStore'
import { useKpiStore } from '@/stores/useKpiStore'
import { Configuration, DefaultConfig } from '@/api-swagger'
import { useSelectionsStore } from '@/stores/useSelectionsStore'
import * as Sentry from '@sentry/vue'
import { useWebSocketStore } from '@/stores/useWebSocketStore'
import { useNotificationWsStore } from '@/stores/useNotificationWsStore'
import { runAuthSchemaGate } from '@/auth/versionGate'
import { initVersionWatcher } from '@/auth/versionWatcher'
import { useTitleStore } from '@/stores/useTitleStore'
import { createBoostCoreFetch } from '@/utils/dPopFetch'
import { initCoreSessionHandler, stopCoreSessionHandler, initLogoutBroadcast } from '@/auth/coreSessionHandler'
import { useSuiteStore } from '@/stores/useSuiteStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { isOriginAllowed } from '@/auth/allowedOrigins'

ModuleRegistry.registerModules([AllCommunityModule])

function renderForbiddenOriginScreen() {
  const root = document.getElementById('app')
  if (!root) return
  root.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;
                font-family:system-ui,sans-serif;background:#f3f4f6;padding:1rem;">
      <div style="max-width:480px;background:#fff;border-radius:12px;padding:2rem;
                  box-shadow:0 10px 25px rgba(0,0,0,.08);text-align:center;">
        <h1 style="color:#dc2626;margin:0 0 .75rem;font-size:1.5rem;">Accès non autorisé</h1>
        <p style="color:#374151;margin:0;">
          Cette application n'est pas accessible depuis cette adresse.
        </p>
      </div>
    </div>
  `
}

async function bootApp(oidcOk: boolean) {
  const pinia = createPinia()
  const app = createApp(App).use(pinia).use(router)

  // Cross-tab logout: any tab that logs out forces all others to /auth/login
  initLogoutBroadcast()

  const suiteStore = useSuiteStore()
  await suiteStore.checkSuiteUrl()

  app.config.globalProperties.$oidc = oidcAuth
  pinia.use(Sentry.createSentryPiniaPlugin())

  DefaultConfig.config = new Configuration({
    basePath: import.meta.env.VITE_SUITE_URL,
    fetchApi: createBoostCoreFetch(),
    credentials: 'include',
  })

  // WS auto-gérée : on instancie le store toujours (login, logout, trickster), il observe boostCoreStore.user
  // et ouvre/ferme la socket en conséquence. Pas besoin d'appel manuel à connect()/disconnect() ailleurs.
  useWebSocketStore()

  // Dedicated notification WS — same lifecycle pattern (auto open/close on
  // boostCoreStore.user). Pushes unread-count updates in real time.
  useNotificationWsStore()

  if (oidcOk && oidcAuth.isAuthenticated) {
    // OIDC session is active — initialize OIDC-specific flows
    initCoreSessionHandler(refreshBoostCoreSession)
    initAuthListeners()

    const userStore = useUserStore()

    if (!userStore.userConnected) {
      userStore.getMe().then((res) => {
        if (res) {
          userStore.userConnected = res
          userStore.getAllUsers()
          const selectionsStore = useSelectionsStore()
          selectionsStore.getAll()
          const campaignStore = useCampaignStore()
          campaignStore.getManagerCampaigns()
          campaignStore.getMyPrivateCampaign()
          const kpiStore = useKpiStore()
          kpiStore.getMyKpisOpportunities()
          const titleStore = useTitleStore()
          titleStore.getTitles()
        }
      })
    }

  }

  if (import.meta.env.VITE_SENTRY_ENVIRONMENT == 'production' || import.meta.env.VITE_SENTRY_ENVIRONMENT == 'development') {
    Sentry.init({
      app,
      release: import.meta.env.VITE_SENTRY_RELEASE,
      dsn: import.meta.env.VITE_SENTRY_DSN,
      integrations: [
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
        Sentry.browserTracingIntegration({ router }),
        Sentry.browserProfilingIntegration(),
        Sentry.vueIntegration({
          tracingOptions: {},
        }),
      ],
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
      normalizeDepth: 10,
      beforeSend(event) {
        const bcStore = useBoostCorePermissionStore()
        event.user = {
          email: bcStore.user?.email ?? oidcAuth.userProfile?.email,
          name: bcStore.user ? `${bcStore.user.firstName ?? ''} ${bcStore.user.lastName ?? ''}`.trim() : oidcAuth.userProfile?.name,
        }
        return event
      },
      tracesSampleRate: 1.0,
      // Domaine courant (multi-domaine, cf oidcAuth appOrigin) + API boostcore configurée.
      tracePropagationTargets: ['localhost', window.location.origin, import.meta.env.VITE_SUITE_URL, import.meta.env.VITE_API_URL].filter(Boolean),
    })
  }

  app.use(VCalendar, {})
  app.use(ToastPlugin, { position: 'top-right', duration: 4000 })
  app.component('AgGridVue', AgGridVue)

  app.mount('#app')

  window.addEventListener('beforeunload', () => {
    stopCoreSessionHandler()
  })

  app.config.errorHandler = (err, _vm, info) => {
    if (typeof err === 'string') {
      Sentry.captureMessage(`Error in ${info}: "${err}"`, 'error')
    }
    if (err instanceof Error) {
      Sentry.captureException(err)
    }
  }
}

// Boot:
//   1. runAuthSchemaGate(): wipes stale auth state if SCHEMA_VERSION bumped
//      (cookies + localStorage + sessionStorage). Runs once per (tab, version).
//   2. initVersionWatcher(): keeps long-lived tabs honest — if a new bundle
//      is deployed while the tab stays open, it reloads automatically when
//      the user comes back to it (visibilitychange) or hourly.
//   3. OIDC startup → bootApp.
if (!isOriginAllowed()) {
  renderForbiddenOriginScreen()
} else {
  runAuthSchemaGate()
    .catch(() => { /* never block boot on the gate */ })
    .then(() => { void initVersionWatcher() })
    .then(() => oidcAuth.startup())
    .then((ok) => bootApp(ok))
}
